import React, { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import InfoOutlineIcon from "@mui/icons-material/InfoOutlined";
import { SxProps, Theme } from "@mui/material/styles";
import { LightTooltip } from "../../../LightTooltip";
import { IntensityFilter, IntensityValue } from "./IntensityFilter";

interface IntensitySectionProps {
  disabled?: boolean;
  value: IntensityValue;
  onChange: (value: IntensityValue) => void;
  filterHasSelection: boolean;
  sx?: SxProps<Theme>;
}

const TOOLTIP_TEXT =
  "Filter companies based on the install intensity of the selected products.";

export const IntensitySection: React.FC<IntensitySectionProps> = React.memo(
  ({ disabled = false, value, onChange, filterHasSelection, sx }) => {
    const hasIntensityValue =
      value.min !== undefined || value.max !== undefined;
    const [intensityEnabled, setIntensityEnabled] = useState(hasIntensityValue);

    useEffect(() => {
      if (hasIntensityValue) {
        setIntensityEnabled(true);
      }

      if (hasIntensityValue === false && disabled) {
        setIntensityEnabled(false);
      }
    }, [hasIntensityValue, disabled]);

    const handleIntensityToggle = useCallback(() => {
      setIntensityEnabled((prev) => {
        const newEnabled = !prev;

        if (!newEnabled) {
          onChange({ min: undefined, max: undefined });
        }

        return newEnabled;
      });
    }, [onChange]);

    return (
      <Box sx={sx}>
        <Stack direction="row" alignItems="center">
          <FormControlLabel
            sx={{ mr: 1 }}
            control={
              <Checkbox
                checked={intensityEnabled}
                onChange={handleIntensityToggle}
                disabled={disabled || !filterHasSelection}
                size="small"
              />
            }
            label="Refine by Intensity"
          />
          <LightTooltip title={TOOLTIP_TEXT} placement="top">
            <InfoOutlineIcon
              fontSize="small"
              sx={{ color: "text.secondary" }}
            />
          </LightTooltip>
        </Stack>

        {intensityEnabled && !disabled && filterHasSelection && (
          <Box mt={1}>
            <IntensityFilter
              value={value}
              onChange={onChange}
              disabled={disabled}
            />
          </Box>
        )}
      </Box>
    );
  }
);

IntensitySection.displayName = "IntensitySection";
